import React from 'react'
import { Alert, Button } from '@mui/material'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { ActionCodeInfo, ActionCodeOperation, User } from 'firebase/auth'
import { TitledPaper } from '@dai/components'
import * as Yup from 'yup'
import ResetPassword, {
  PasswordResetLabels,
} from './reset-password/ResetPassword'
import RecoverEmail from './recover-email/RecoverEmail'
import VerifyEmail from './verify-email/VerifyEmail'
import VerifyAndChangeEmail from './verify-and-change-email/VerifyAndChangeEmail'

interface ErrorComponentProps {
  error: string
  signInUrl: string
}

const ErrorComponent: React.FC<ErrorComponentProps> = ({
  error,
  signInUrl,
}) => (
  <TitledPaper title="Error" subtitle="We couldn't process your request">
    <Alert severity="error">{error}</Alert>
    <Button
      fullWidth
      color="primary"
      variant="contained"
      component={RouterLink}
      to={signInUrl}
      sx={{ mt: 2 }}
    >
      Return to Sign In
    </Button>
  </TitledPaper>
)

/**
 * Maps the URL 'mode' parameter to the corresponding ActionCodeOperation enum value
 * @param mode - The URL 'mode' parameter
 * @returns The ActionCodeOperation enum value
 */
const mapModeToActionCodeOperation = (
  mode: string | null | undefined,
): keyof typeof ActionCodeOperation | null => {
  switch (mode) {
    case 'resetPassword':
      return ActionCodeOperation.PASSWORD_RESET
    case 'recoverEmail':
      return ActionCodeOperation.RECOVER_EMAIL
    case 'verifyEmail':
      return ActionCodeOperation.VERIFY_EMAIL
    case 'verifyAndChangeEmail':
      return ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL
    case 'revertSecondFactorAddition':
      return ActionCodeOperation.REVERT_SECOND_FACTOR_ADDITION
    case 'signIn':
      return ActionCodeOperation.EMAIL_SIGNIN
    default:
      return null
  }
}

interface FirebaseAuthActionHandlerProps {
  mode: string | null
  oobCode: string | null
  signInUrl: string
  signUpUrl: string
  continueUrl?: string
  /**
   * Yup schema for password validation. Make sure this matches the password policy in the
   * project's Firebase Console at `Authentication > Settings > Password Policy`.
   */
  passwordSchema?: Yup.StringSchema
  checkActionCode: (code: string) => Promise<ActionCodeInfo>
  confirmPasswordReset: (oobCode: string, password: string) => Promise<void>
  sendPasswordResetEmail: (
    email: string,
    continueUrl?: string | undefined,
  ) => Promise<void>
  sendEmailVerification: () => Promise<void>
  verifyEmail: (oobCode: string) => Promise<void>
  user: User | null
  isAuthenticated: boolean
  signOut: () => Promise<void>
  verifyAndRecoverEmail: (oobCode: string) => Promise<void>
  sendVerifyBeforeUpdateEmail: (
    newEmail: string,
    continueUrl?: string | undefined,
  ) => Promise<void>
  verifyAndUpdateEmail: (oobCode: string) => Promise<void>
  labels?: {
    passwordReset?: PasswordResetLabels
  }
}

const FirebaseAuthActionHandler: React.FC<FirebaseAuthActionHandlerProps> = ({
  mode,
  oobCode,
  continueUrl = '/',
  signInUrl,
  signUpUrl,
  passwordSchema = Yup.string()
    .min(6, 'Must be at least 6 characters')
    .max(255),
  checkActionCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
  sendEmailVerification,
  verifyEmail,
  user,
  isAuthenticated,
  signOut,
  verifyAndRecoverEmail,
  sendVerifyBeforeUpdateEmail,
  verifyAndUpdateEmail,
  labels,
}) => {
  const actionCodeOperation = mapModeToActionCodeOperation(mode)

  const location = useLocation()
  const navigate = useNavigate()
  const navigateToUpdateEmail = () => {
    navigate(location.pathname + '?mode=verifyAndChangeEmail', {
      state: { from: location.pathname + location.search },
      replace: true,
    })
  }

  switch (actionCodeOperation) {
    case ActionCodeOperation.PASSWORD_RESET:
      return (
        <ResetPassword
          oobCode={oobCode}
          signInUrl={signInUrl}
          signUpUrl={signUpUrl}
          passwordSchema={passwordSchema}
          checkActionCode={checkActionCode}
          confirmPasswordReset={confirmPasswordReset}
          sendPasswordResetEmail={sendPasswordResetEmail}
          labels={labels?.passwordReset}
        />
      )
    case ActionCodeOperation.RECOVER_EMAIL:
      return oobCode ? (
        <RecoverEmail
          oobCode={oobCode}
          signInUrl={signInUrl}
          checkActionCode={checkActionCode}
          verifyAndRecoverEmail={verifyAndRecoverEmail}
        />
      ) : (
        <ErrorComponent
          error="Invalid action parameters."
          signInUrl={signInUrl}
        />
      )
    case ActionCodeOperation.VERIFY_EMAIL:
      return (
        <VerifyEmail
          oobCode={oobCode}
          continueUrl={continueUrl}
          signInUrl={signInUrl}
          navigateToUpdateEmail={navigateToUpdateEmail}
          sendEmailVerification={sendEmailVerification}
          user={user}
          isAuthenticated={isAuthenticated}
          verifyEmail={verifyEmail}
        />
      )
    case ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL:
      return (
        <VerifyAndChangeEmail
          oobCode={oobCode}
          signInUrl={signInUrl}
          isAuthenticated={isAuthenticated}
          signOut={signOut}
          verifyAndUpdateEmail={verifyAndUpdateEmail}
          checkActionCode={checkActionCode}
          sendVerifyBeforeUpdateEmail={sendVerifyBeforeUpdateEmail}
        />
      )
    default:
      return (
        <ErrorComponent
          error="The requested action is not supported."
          signInUrl={signInUrl}
        />
      )
  }
}

export default FirebaseAuthActionHandler
